import type { ContestEntryDTO } from '@shared/protocol/messages';
import { validateMonster } from '../combat/budget';
import { t } from '../../i18n';
import { monsterFromDTO } from './monsters';
import type { Monster, SaveData } from './model';
import { addMemory } from './progression';

/** Coins for the first vote of each weekly round. */
export const CONTEST_VOTE_COINS = 5;
export const CHAMPION_STAMP = 'stamp:contest_winner';
/** Rounds remembered for the first-vote bonus (older ones can never come back). */
const KEEP_ROUNDS = 12;

/** My own drawn monsters that may enter: public ones (classroom monsters are practice only). */
export function contestCandidates(save: SaveData): Monster[] {
  return save.monsters.filter((m) => m.source === 'mine' && m.mode === 'public' && validateMonster(m).ok);
}

/** Pays the first-vote bonus once per round; returns the coins given (0 if already paid). */
export function rewardFirstVote(save: SaveData, round: string): number {
  if (save.contest.votedRounds.includes(round)) return 0;
  save.contest.votedRounds.push(round);
  if (save.contest.votedRounds.length > KEEP_ROUNDS) save.contest.votedRounds.splice(0, save.contest.votedRounds.length - KEEP_ROUNDS);
  save.coins += CONTEST_VOTE_COINS;
  return CONTEST_VOTE_COINS;
}

export const hallMonsterId = (entryId: string): string => `hall:${entryId}`;

/**
 * Tapes every Hall of Fame winner into the notebook as an official monster
 * (fightable like the story ones). Updates keep the player's win/loss record.
 * Entries that fail the client budget check are skipped.
 */
export function syncHall(save: SaveData, hall: readonly ContestEntryDTO[]): Monster[] {
  const out: Monster[] = [];
  for (const e of hall) {
    if (e.status !== 'winner' || !validateMonster(e.monster).ok) continue;
    const id = hallMonsterId(e.id);
    const fresh = monsterFromDTO(e.monster, 'hall', e.authorName);
    fresh.id = id;
    fresh.createdAt = Date.parse(e.createdAt) || 0;
    const old = save.monsters.find((m) => m.id === id);
    if (old) Object.assign(old, { ...fresh, wins: old.wins, losses: old.losses });
    else save.monsters.push(fresh);
    out.push(old ?? fresh);
  }
  return out;
}

/** Gives the champion stamp and a memory for each of my winners not rewarded yet. */
export function claimChampionPrizes(save: SaveData, hall: readonly ContestEntryDTO[]): ContestEntryDTO[] {
  const won = hall.filter((e) => e.mine && e.status === 'winner' && !save.contest.prizes.includes(e.id));
  for (const e of won) {
    save.contest.prizes.push(e.id);
    if (!save.cosmetics.owned.includes(CHAMPION_STAMP)) save.cosmetics.owned.push(CHAMPION_STAMP);
    addMemory(save, {
      kind: 'quest',
      title: t('{monster} won the monster contest!', { monster: e.monster.name }),
      text: t('Everyone voted, and now it lives in the Hall of Fame (round {round}).', { round: e.round }),
      sticker: 'crown',
    });
  }
  return won;
}
